import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import type { MapView } from "../../api/dataReview";
import * as geojson from "geojson";
import { Remainder } from "../../types/dataReview";

/** Where the vegetation editor is: on its home panel, setting up a new
 *  refinement, or inside one (reviewing findings, editing one outline, or
 *  painting). Shared by the bar, the floating panels and the map tools. */
export type EditorPhase = "home" | "setup" | "session";
export type EditorMode = "review" | "edit" | "paint";
export type EditorTool = "select" | "lasso" | "wand" | "unwand" | "draw" | "cut" | "brush" | "poly" | "revert";

/** One thing asked for, waiting for the run to be ready for it. */
export interface QueuedTerm {
  key?: string; phrase?: string; label: string; target: string; asset_type: string;
  /** Sent to the server: this is the pass under way, shown as running. */
  sent?: boolean;
  /** The run's pass number when it was sent; a higher one means it finished. */
  sentPass?: number;
  /** The run has been seen busy since it was sent. */
  seenBusy?: boolean;
  /** When the run was seen back in review; the item goes once findings newer than this arrive. */
  settledAt?: number;
}

export interface SetupState {
  areaMode: "project" | "draw";
  drawPoly: geojson.Feature | null;
  startMode: "clean" | "build";
  keepBuildings: boolean;
}

interface Toast { msg: string; undo?: () => void }

interface VegEditorState {
  phase: EditorPhase; setPhase: (p: EditorPhase) => void;
  sessionId: number | null; setSessionId: (id: number | null) => void;
  mode: EditorMode; setMode: (m: EditorMode) => void;
  tool: EditorTool; setTool: (t: EditorTool) => void;
  activeTerm: string | null; setActiveTerm: (t: string | null) => void;
  queued: QueuedTerm[]; setQueued: (q: QueuedTerm[] | ((q: QueuedTerm[]) => QueuedTerm[])) => void;
  sheet: null | "changes"; setSheet: (s: null | "changes") => void;
  before: boolean; setBefore: (b: boolean) => void;
  setup: SetupState; patchSetup: (p: Partial<SetupState>) => void;
  paintClass: string; setPaintClass: (c: string) => void;
  /** How many paint ops the run had when paint mode was entered; Cancel reverts to here. */
  paintMark: number; setPaintMark: (n: number) => void;
  brushM: number; setBrushM: (m: number) => void;
  tolerance: number; setTolerance: (t: number) => void;
  remainder: Remainder | null; setRemainder: (r: Remainder | null) => void;
  layersOpen: boolean; setLayersOpen: (v: boolean) => void;
  /** The map's view (padded), for fetching only the findings in it. */
  view: MapView | null; setView: (v: MapView | null) => void;
  confirmExit: boolean; setConfirmExit: (v: boolean) => void;
  toast: Toast | null; showToast: (msg: string, undo?: () => void) => void; clearToast: () => void;
  error: string | null; setError: (e: string | null) => void;
}

const noop = () => {};
const VegEditorContext = createContext<VegEditorState>({
  phase: "home", setPhase: noop, sessionId: null, setSessionId: noop, mode: "review", setMode: noop, tool: "select", setTool: noop,
  activeTerm: null, setActiveTerm: noop, queued: [], setQueued: noop, sheet: null, setSheet: noop, before: false, setBefore: noop,
  setup: { areaMode: "project", drawPoly: null, startMode: "build", keepBuildings: true }, patchSetup: noop,
  paintClass: "Lawn", setPaintClass: noop, paintMark: 0, setPaintMark: noop, brushM: 6, setBrushM: noop, tolerance: 15, setTolerance: noop,
  remainder: null, setRemainder: noop,
  layersOpen: false, setLayersOpen: noop, view: null, setView: noop, confirmExit: false, setConfirmExit: noop, toast: null, showToast: noop, clearToast: noop,
  error: null, setError: noop,
});

export const useVegEditor = () => useContext(VegEditorContext);

export const VegEditorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [phase, setPhase] = useState<EditorPhase>("home");
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [mode, setMode] = useState<EditorMode>("review");
  const [tool, setTool] = useState<EditorTool>("select");
  const [activeTerm, setActiveTerm] = useState<string | null>(null);
  const [queued, setQueued] = useState<QueuedTerm[]>([]);
  const [sheet, setSheet] = useState<null | "changes">(null);
  const [before, setBefore] = useState(false);
  const [setup, setSetup] = useState<SetupState>({ areaMode: "project", drawPoly: null, startMode: "build", keepBuildings: true });
  const patchSetup = useCallback((p: Partial<SetupState>) => setSetup((s) => ({ ...s, ...p })), []);
  const [paintClass, setPaintClass] = useState("Lawn");
  const [paintMark, setPaintMark] = useState(0);
  const [brushM, setBrushM] = useState(6);
  const [tolerance, setTolerance] = useState(15);
  const [remainder, setRemainder] = useState<Remainder | null>(null);
  const [layersOpen, setLayersOpen] = useState(false);
  const [view, setView] = useState<MapView | null>(null);
  const [confirmExit, setConfirmExit] = useState(false);
  const [toast, setToast] = useState<Toast | null>(null);
  const [error, setError] = useState<string | null>(null);
  const toastTimer = useRef<number | null>(null);
  const showToast = useCallback((msg: string, undo?: () => void) => {
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    setToast({ msg, undo });
    toastTimer.current = window.setTimeout(() => setToast(null), undo ? 9000 : 4000);
  }, []);
  const clearToast = useCallback(() => { if (toastTimer.current) window.clearTimeout(toastTimer.current); setToast(null); }, []);

  const value = useMemo<VegEditorState>(() => ({
    phase, setPhase, sessionId, setSessionId, mode, setMode, tool, setTool, activeTerm, setActiveTerm, queued, setQueued,
    sheet, setSheet, before, setBefore, setup, patchSetup, paintClass, setPaintClass, paintMark, setPaintMark, brushM, setBrushM, tolerance, setTolerance,
    remainder, setRemainder, layersOpen, setLayersOpen, view, setView,
    confirmExit, setConfirmExit, toast, showToast, clearToast, error, setError,
  }), [phase, sessionId, mode, tool, activeTerm, queued, sheet, before, setup, patchSetup, paintClass, paintMark, brushM, tolerance,
    remainder, layersOpen, view, confirmExit, toast, showToast, clearToast, error]);
  return <VegEditorContext.Provider value={value}>{children}</VegEditorContext.Provider>;
};
